import React from 'react';

/**
 * The traffic light, and the vocabulary that goes with it.
 *
 * Deliberately the same four words the `bridge.health` data stream publishes —
 * healthy / degraded / stale / down — and the same connected/connecting/
 * disconnected pill shape `TopBar` already uses for WebSocket, vMix and
 * Singular. A graphics operator reads this without learning anything new, which
 * matters more than it sounds at 21:00 on a finals night.
 */

export const HEALTH_COLOURS = {
  healthy: { dot: 'bg-green-500', text: 'text-green-400', border: 'border-green-700', bg: 'bg-green-900/20' },
  degraded: { dot: 'bg-amber-500', text: 'text-amber-400', border: 'border-amber-700', bg: 'bg-amber-900/20' },
  stale: { dot: 'bg-orange-500', text: 'text-orange-400', border: 'border-orange-700', bg: 'bg-orange-900/20' },
  down: { dot: 'bg-red-500', text: 'text-red-400', border: 'border-red-700', bg: 'bg-red-900/20' },
};

export const HEALTH_MEANING = {
  healthy: 'Polling and publishing normally.',
  degraded: 'Still on air with good data, but something is failing — check Diagnostics.',
  stale: 'No successful poll recently. Graphics are showing held data and saying so.',
  down: 'Not receiving data. Graphics keep the last payload Singular received.',
};

export function HealthPill({ state = 'down', className = '' }) {
  const colour = HEALTH_COLOURS[state] || HEALTH_COLOURS.down;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[11px] font-medium ${colour.bg} ${colour.text} border ${colour.border} ${className}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${colour.dot}`} />
      {state}
    </span>
  );
}

/** The big one, for the Live screen. Readable across a gallery. */
export function HealthLight({ health }) {
  const state = health?.state || 'down';
  const colour = HEALTH_COLOURS[state] || HEALTH_COLOURS.down;

  return (
    <div className={`flex items-center gap-4 px-5 py-4 rounded-lg border ${colour.border} ${colour.bg}`}>
      <span className={`w-4 h-4 rounded-full ${colour.dot} flex-shrink-0`} />
      <div className="min-w-0">
        <div className={`text-lg font-bold uppercase tracking-wide ${colour.text}`}>
          {state}
        </div>
        <div className="text-xs text-gray-400 mt-0.5">
          {health?.upstream?.lastError || HEALTH_MEANING[state]}
        </div>
      </div>
    </div>
  );
}

/** One number with a label. The Live screen is mostly these. */
export function Metric({ label, value, hint, tone = 'default' }) {
  const tones = {
    default: 'text-white',
    warn: 'text-amber-400',
    bad: 'text-red-400',
    good: 'text-green-400',
    muted: 'text-gray-500',
  };
  return (
    <div className="bg-[#111111] border border-[#2a2a2a] rounded px-3 py-2.5">
      <div className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">{label}</div>
      <div className={`text-sm font-semibold mt-0.5 tabular-nums ${tones[tone]}`}>{value ?? '—'}</div>
      {hint && <div className="text-[10px] text-gray-600 mt-0.5">{hint}</div>}
    </div>
  );
}
